import React from 'react'

const DEFAULT_ITEMS_PER_PAGE = 30
const DEFAULT_NUMBER_OF_PAGE_BUTTONS = 10

export interface TablePaginationProps<T> {
  canGoBack: boolean
  goBack: () => void
  canGoForward: boolean
  goForward: () => void
  goToPage: (page: number) => void
  canGoToPage: (page: number) => boolean
  currentPage: number
  pageCount: number
  visiblePages: number[]
  paginatedData: T
}

interface TablePaginationOptions<T> {
  data: T[]
  itemsPerPage?: number
  numberOfPageButtons?: number
  shouldResetScroll?: boolean
}

export function useTablePagination<T>({
  data,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  numberOfPageButtons = DEFAULT_NUMBER_OF_PAGE_BUTTONS,
  shouldResetScroll,
}: TablePaginationOptions<T>): TablePaginationProps<T[]> {
  const [currentPage, setPage] = React.useState<number>(0)
  const pageCount = Math.ceil((data?.length || 0) / itemsPerPage)

  // Reset currentPage to 0 when data changes
  React.useEffect(() => setPage(0), [data])

  const pagination = React.useMemo(() => {
    const canGoToPage = (page: number) => page - 1 >= 0 && page - 1 < pageCount
    const setPageState = (page: React.SetStateAction<number>) => {
      setPage(page)
      if (shouldResetScroll) {
        window.scrollTo({ top: 0 })
      }
    }

    let start = Math.max(0, currentPage - Math.floor(numberOfPageButtons / 2))
    const end = Math.min(pageCount - 1, start + numberOfPageButtons - 1)
    if (end - start + 1 < numberOfPageButtons) {
      start = Math.max(0, end - numberOfPageButtons + 1)
    }

    return {
      canGoToPage,
      goBack: () => setPageState((prevPage) => Math.max(prevPage - 1, 0)),
      goForward: () => setPageState((prevPage) => Math.min(prevPage + 1, pageCount - 1)),
      goToPage: (page: number) => {
        if (canGoToPage(page)) setPageState(page - 1)
      },
      visiblePages: Array.from({ length: end - start + 1 }, (_, index) => start + index + 1),
    }
  }, [pageCount, numberOfPageButtons, currentPage, shouldResetScroll])

  const paginatedData = React.useMemo(() => {
    const start = currentPage * itemsPerPage
    const end = start + itemsPerPage
    return data.slice(start, end)
  }, [data, currentPage, itemsPerPage])

  return {
    ...pagination,
    canGoBack: currentPage > 0,
    canGoForward: currentPage < pageCount - 1,
    currentPage: currentPage + 1,
    pageCount,
    paginatedData,
  }
}

export enum PageItems {
  gap = 'gap',
  prev = 'prev',
  next = 'next',
  pageNumber = 'pageNumber',
}

export function getNumberOfSteps(pages: number) {
  const step = Math.ceil(pages / 10)
  if (step <= 1) {
    return 1
  } else if (step <= 10) {
    return 10
  } else if (step <= 50) {
    return 50
  } else {
    return 100
  }
}

export function getStepPagination({
  pages,
  currentPage,
  maxSidePreviewPages = 5,
}: {
  pages: number
  currentPage: number
  maxSidePreviewPages?: number
}) {
  const items: Array<PageItems.gap | number> = []
  const step = getNumberOfSteps(pages)

  let prevPage = 0
  for (let p = 1; p <= pages; ++p) {
    if (p !== currentPage) {
      if (p === 1 || p === pages || Math.abs(p - currentPage) <= maxSidePreviewPages || p % step === 0) {
        if (prevPage !== p - 1) {
          items.push(PageItems.gap)
        }
        items.push(p)
        prevPage = p
      }
    } else {
      items.push(p)
      prevPage = p
    }
  }

  return items
}
